// 028 — Test de Denver II (Serie 028, Fase 2, Tanda 11, ver PLAN.md /
// tasks/investigacion-028-aiepi-denver.md). Cierra la Serie 028 completa.
// No tenía ningún rastro en el código (zona roja de la auditoría original) —
// fuente primaria confirmada con evidencia total en las páginas 114-115 del
// PDF oficial AM-00115-2021 (índice 55 formularios). Componente bespoke,
// molde del odontograma (033): la cuadrícula de 4 franjas con ~106 ítems no
// es una curva de percentil (motor de Fase 1/`recharts`) — es geometría fija
// del formulario impreso + interacción, igual que el odontograma.
//
// Adaptación oficial ecuatoriana del Test de Denver II (Denver Developmental
// Materials Inc.) — se implementa citando la fuente regulatoria
// (`MSP/DNEAIS-HCU-form.028/mayo/2015`), no una tabla de otro editor
// (decisión ya tomada por Sue, ver PLAN.md). Ver el header de
// `packages/hce-pdf/src/denverII.ts` para la limitación conocida de
// precisión de posición de cada ítem (transcripción visual de un formulario
// escaneado, no medición de píxel).
import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { useMedplum, useResource } from '@medplum/react-hooks';
import type { Patient } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { CLASIFICACIONES_DENVER, formatearEdadCronologicaDenver, type ClasificacionDenver, type DatosDenverPdf, type ResultadoItemDenver } from '@logiacare/hce-pdf';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { FormularioDiscretoProps, Par } from './index';
import { CampoDictado } from './CampoDictado';
import { DenverIIVisual } from './denverII/DenverIIVisual';
import { clasificacionRequiereCondition, construirObservationDenver } from './denverII/fhir';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional } from './transaccionFhir';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';

const CODIGO = '028';
const CITA_LEGAL_DENVER = 'MSP/DNEAIS-HCU-form.028/mayo/2015';

/** Edad en meses completos entre nacimiento y hoy — mismo divisor (30.4375 días/mes) que `curvaPercentilPorEdad.ts` para la Serie 028. */
function edadActualEnMeses(birthDate: string | undefined): number | undefined {
  if (!birthDate) return undefined;
  const dias = (Date.now() - new Date(birthDate).getTime()) / (1000 * 60 * 60 * 24);
  if (dias < 0) return undefined;
  return Math.round(dias / 30.4375);
}

export function Formulario028({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const patient = useResource<Patient>(subject);
  const { guardando, error, guardar } = useGuardado();

  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const titulo = t('formularios.f028.titulo');
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, CODIGO, titulo, { citaLegalCompleta: CITA_LEGAL_DENVER });

  const edadPorDefecto = useMemo(() => edadActualEnMeses(patient?.birthDate), [patient?.birthDate]);
  const [edadEvaluacionMeses, setEdadEvaluacionMeses] = useState<string>('');
  const edadEfectiva = edadEvaluacionMeses !== '' ? Number(edadEvaluacionMeses) : edadPorDefecto;

  const [edadGestacional, setEdadGestacional] = useState<string>('');
  const gestacionalNumero = edadGestacional !== '' ? Number(edadGestacional) : undefined;
  // Fuera de 20-45 semanas es un error de captura: no se guarda ni se imprime.
  const edadGestacionalSemanas = gestacionalNumero !== undefined && Number.isInteger(gestacionalNumero) && gestacionalNumero >= 20 && gestacionalNumero <= 45 ? gestacionalNumero : undefined;

  const [estadoItems, setEstadoItems] = useState<Readonly<Record<string, ResultadoItemDenver>>>({});
  const [clasificacion, setClasificacion] = useState<ClasificacionDenver | ''>('');
  const [observaciones, setObservaciones] = useState('');

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const clasificacionFinal = clasificacion || undefined;
      const observation = construirObservationDenver(estadoItems, edadEfectiva, clasificacionFinal, subject, encounter, edadGestacionalSemanas);
      const lote = new LoteTransaccional();
      if (observation) {
        lote.crear(observation);
      }
      if (clasificacionRequiereCondition(clasificacionFinal)) {
        lote.crear({
          resourceType: 'Condition',
          subject,
          encounter,
          code: { text: `Test de Denver II — clasificación ${CLASIFICACIONES_DENVER.find((c) => c.clave === clasificacionFinal)?.etiqueta}` },
          recordedDate: new Date().toISOString(),
        });
      }
      await lote.enviar(medplum);

      const pares: Par[] = [
        { pregunta: t('formularios.f028.edadGestacional'), respuesta: edadGestacionalSemanas !== undefined ? String(edadGestacionalSemanas) : '' },
        { pregunta: t('formularios.f028.edadCronologica'), respuesta: edadEfectiva !== undefined ? `${formatearEdadCronologicaDenver(edadEfectiva)} (${Math.round(edadEfectiva)} ${t('formularios.f028.meses')})` : '' },
        { pregunta: t('formularios.f028.clasificacionPresuntiva'), respuesta: clasificacionFinal ? t(`formularios.f028.clasificaciones.${clasificacionFinal}`) : '' },
        { pregunta: t('formularios.f028.observaciones'), respuesta: observaciones.trim() },
      ].filter((p) => p.respuesta.trim() !== '');

      const diagramaDenver: DatosDenverPdf = {
        entradas: Object.entries(estadoItems).map(([itemId, resultado]) => ({ itemId, resultado })),
        edadEvaluacionMeses: edadEfectiva,
        edadGestacionalSemanas,
        clasificacionPresuntiva: clasificacionFinal,
      };
      onGuardado(pares, datosEncabezadoMsp, undefined, undefined, diagramaDenver);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{titulo}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <div className="flex flex-col gap-1">
          <Label>{t('formularios.f028.edadGestacional')}</Label>
          <Input type="number" min={20} max={45} value={edadGestacional} onChange={(e) => setEdadGestacional(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1">
          <Label>{t('formularios.f028.edadEvaluacion')}</Label>
          <Input type="number" min={0} placeholder={edadPorDefecto !== undefined ? String(edadPorDefecto) : ''} value={edadEvaluacionMeses} onChange={(e) => setEdadEvaluacionMeses(e.target.value)} />
          {edadEfectiva !== undefined && (
            <p className="text-xs text-hce-muted">
              {t('formularios.f028.edadCronologica')}: {formatearEdadCronologicaDenver(edadEfectiva)}
            </p>
          )}
        </div>
      </div>

      <DenverIIVisual estado={estadoItems} onChange={setEstadoItems} edadEvaluacionMeses={edadEfectiva} />

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <div className="flex flex-col gap-1">
          <Label>{t('formularios.f028.clasificacionPresuntiva')}</Label>
          <Select value={clasificacion || undefined} onValueChange={(v) => setClasificacion(v as ClasificacionDenver)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CLASIFICACIONES_DENVER.map((c) => (
                <SelectItem key={c.clave} value={c.clave}>
                  {t(`formularios.f028.clasificaciones.${c.clave}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <CampoDictado id="f028-observaciones" etiqueta={t('formularios.f028.observaciones')} valor={observaciones} onChange={setObservaciones} />

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
